import React, { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import * as Icons from "lucide-react";
import { NAV_ITEMS } from "@/constants/nav";
import { LAYOUT } from "@/constants/testIds";
import { cn } from "@/lib/utils";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

const Icon = ({ name, className }) => {
  const Cmp = Icons[name] || Icons.Circle;
  return <Cmp className={className} aria-hidden="true" />;
};

function itemClass(isActive, nested) {
  return cn(
    "sidebar-nav-item flex !flex-row flex-nowrap items-center gap-2.5 w-full min-w-0 px-3 rounded-lg text-sm leading-none whitespace-nowrap no-underline",
    nested ? "h-8 text-[13px] pl-8" : "h-9",
    isActive
      ? "bg-white/[0.06] text-white shadow-[inset_0_0_0_1px_rgba(139,92,246,0.35)]"
      : "text-white/60 hover:text-white hover:bg-white/[0.03]"
  );
}

function NavGlyph({ name }) {
  return (
    <span className="inline-flex h-4 w-4 shrink-0 items-center justify-center">
      <Icon name={name} className="h-4 w-4" />
    </span>
  );
}

function NavItem({ it, collapsed, onNavigate, nested }) {
  if (!it.to) return null;
  const link = (
    <NavLink
      to={it.to}
      end={it.to === "/" || it.to === "/seo"}
      data-testid={`nav-${it.key}`}
      onClick={onNavigate}
      title={collapsed ? it.label : undefined}
      className={({ isActive }) => itemClass(isActive, nested)}
    >
      <NavGlyph name={it.icon} />
      {!collapsed && <span className="min-w-0 truncate">{it.label}</span>}
    </NavLink>
  );

  if (!collapsed) return link;

  return (
    <Tooltip delayDuration={150}>
      <TooltipTrigger asChild>
        <div className="w-full">{link}</div>
      </TooltipTrigger>
      <TooltipContent side="right" className="bg-[#1A1A2E] text-white border border-white/10">
        {it.label}
      </TooltipContent>
    </Tooltip>
  );
}

function NavBranch({ it, collapsed, onNavigate }) {
  const location = useLocation();
  const childActive = (it.children || []).some((c) => c.to && location.pathname.startsWith(c.to));
  const [open, setOpen] = useState(childActive);
  useEffect(() => {
    if (childActive) setOpen(true);
  }, [childActive]);
  if (collapsed) {
    return (
      <div className="space-y-0.5">
        {(it.children || []).map((child) => <NavItem key={child.key} it={child} collapsed onNavigate={onNavigate} />)}
      </div>
    );
  }
  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={cn(itemClass(childActive, false), "text-left")}
        aria-expanded={open}
      >
        <NavGlyph name={it.icon} />
        <span className="min-w-0 flex-1 truncate">{it.label}</span>
        <Icons.ChevronDown className={cn("h-3.5 w-3.5 shrink-0 text-white/40 transition-transform", open && "rotate-180")} />
      </button>
      {open && (
        <div className="mt-0.5 space-y-0.5">
          {(it.children || []).map((child) => <NavItem key={child.key} it={child} collapsed={false} onNavigate={onNavigate} nested />)}
        </div>
      )}
    </div>
  );
}

export default function Sidebar({ collapsed, onToggle, mobileOpen, onNavigate }) {
  return (
    <aside
      data-testid={LAYOUT.sidebar}
      className={cn(
        "fixed left-0 top-0 z-30 h-screen flex flex-col glass-strong transition-[width,transform] duration-200",
        collapsed ? "w-[68px]" : "w-[260px]",
        mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
      )}
      aria-label="Primary navigation"
    >
      <div className="flex flex-row items-center gap-2 px-4 h-16 border-b border-white/[0.06]">
        <img src="/cas-logo.png" alt="CAS Media" className="h-8 w-8 object-contain rounded-lg shrink-0" />
        {!collapsed && (
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold tracking-tight text-white leading-tight">CAS Media</div>
            <div className="text-[10px] uppercase tracking-widest text-white/40">Command Center</div>
          </div>
        )}
        <button
          data-testid={LAYOUT.sidebarToggle}
          onClick={onToggle}
          className="ml-auto p-1.5 rounded-md hover:bg-white/5 text-white/50 hover:text-white transition-colors duration-200 hidden lg:inline-flex"
          aria-label="Toggle sidebar"
        >
          <Icons.PanelLeft className="w-4 h-4" />
        </button>
      </div>

      <nav className="flex-1 overflow-y-auto overflow-x-hidden py-3 px-2">
        {NAV_ITEMS.map((sec) => (
          <div key={sec.section} className="mb-4">
            {!collapsed && (
              <div className="px-3 pb-1 text-[10px] uppercase tracking-[0.14em] text-white/35 font-medium">
                {sec.section}
              </div>
            )}
            <div className="space-y-0.5">
              {sec.items.map((it) => (
                it.children
                  ? <NavBranch key={it.key} it={it} collapsed={collapsed} onNavigate={onNavigate} />
                  : <NavItem key={it.key} it={it} collapsed={collapsed} onNavigate={onNavigate} />
              ))}
            </div>
          </div>
        ))}
      </nav>
    </aside>
  );
}
